import React, { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { get } from 'lodash';

import Menu from '../Menu/Menu';
import MenuItem from '../MenuItem/MenuItem';
import { getActivePathValue, getPathDestination } from './Navbar';
import { stripQuery } from '../../resources/helper';

const HIDE_DELAY = 100;

const MainButton = styled(ListItemButton, { shouldForwardProp: (prop) => prop !== 'open' && prop !== 'openSub' && prop !== 'toTop' })(
  ({ open, openSub, toTop }) => ({
    minHeight: 52,
    margin: '0 4px',
    borderRadius: 4,
    paddingLeft: open ? '20px' : '13px',
    paddingRight: '20px',
    justifyContent: 'flex-start',
    /* width: 52, */

    ...(openSub && {
      borderRadius: open ? (toTop ? '0 0 5px 5px' : '5px 5px 0 0') : 5,
      background: '#004A4E',

      '&:hover': {
        background: '#004A4E',
      },
    }),
  }),
);

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: 2,
    top: -5,
    border: `1px solid ${theme.palette.common.white}`,
    fontSize: 10,
    padding: '0 4px',
    backgroundColor: 'blue',
    color: theme.palette.common.white,
  },
}));

function NavbarMenu({ selected, opened, onClick, label, Icon, openedMenu, items, subMenu, toTop, overlapIconText }) {
  items = items ? items.filter((item) => get(item, 'available', true)) : items;
  const navigate = useNavigate();
  const location = useLocation();

  const [openSubmenu, setOpenSubmenu] = useState(false);

  const pointerOverDropdown = useRef(false);
  const pointerOverTarget = useRef(false);
  const anchorEl = useRef(null);

  const handleHover = (event) => {
    const target = event.currentTarget;

    pointerOverTarget.current = true;
    setTimeout(() => {
      if (pointerOverTarget.current) {
        setOpenSubmenu(true);
        pointerOverTarget.current = true;
        anchorEl.current = target;
      }
    }, HIDE_DELAY);
  };

  const handleRequestClose = () => {
    pointerOverTarget.current = false;
    setTimeout(() => {
      if (!pointerOverDropdown.current) {
        setOpenSubmenu(false);
      }
    }, HIDE_DELAY);
  };

  const handlePopoverHover = () => (pointerOverDropdown.current = true);

  const handleRequestPopoverClose = () => {
    pointerOverDropdown.current = false;
    setTimeout(() => {
      if (!pointerOverTarget.current) {
        setOpenSubmenu(false);
      }
    }, HIDE_DELAY);
  };

  const getExpandArrow = (items, opened, openedMenu) => {
    if (items && items.length && opened) {
      if (openedMenu) {
        return toTop ? <ExpandMore /> : <ExpandLess />;
      }

      return toTop ? <ExpandLess /> : <ExpandMore />;
    }

    return null;
  };

  const createMenuItems = (items) => {
    const activePathValue = getActivePathValue(location);

    return items
      .filter((item) => item)
      .map((item, index) => {
        const itemPath = stripQuery(getPathDestination(item.path));

        if (item.items) {
          return (
            <NavbarMenu
              {...item}
              key={itemPath}
              selected={item.items.filter((item) => getPathDestination(item.path) === activePathValue).length > 0}
              subMenu
            />
          );
        }

        const CustomComponent = get(item, 'CustomComponent', null);

        if (!CustomComponent) {
          return (
            <MenuItem
              key={itemPath}
              id={itemPath}
              disabled={item.disabled}
              className={itemPath === activePathValue ? 'selectedNavbarMenuItem' : ''}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </MenuItem>
          );
        }

        return <CustomComponent key={`${index}_custom`} opened={opened} />;
      });
  };

  return (
    <>
      {subMenu ? (
        <MenuItem
          className={selected ? 'selectedNavbarMenuItem' : ''}
          onClick={onClick}
          onMouseEnter={handleHover}
          onMouseLeave={handleRequestClose}
        >
          {label}
          {items && items.length > 0 && <ExpandMore style={{ rotate: '-90deg' }} />}
        </MenuItem>
      ) : (
        <MainButton
          selected={selected}
          onClick={onClick}
          onMouseEnter={handleHover}
          onMouseLeave={handleRequestClose}
          open={opened}
          toTop={toTop}
          openSub={openedMenu && items}
          className={`main-button${pointerOverTarget.current || pointerOverDropdown.current || (openedMenu && !selected) ? ' hovered' : ''}${items && items.length ? ' has-submenu' : ''}${openedMenu ? ' expanded' : ''}${!opened ? ' mini' : ''}${toTop ? ' totop' : ''}`}
        >
          <ListItemIcon
            title={label}
            sx={{
              minWidth: 0,
              mr: opened ? '10px' : 0,
              justifyContent: 'center',
            }}
            className="main-button-icon"
          >
            {overlapIconText ? (
              <StyledBadge badgeContent={overlapIconText}>
                <Icon />
              </StyledBadge>
            ) : (
              <Icon />
            )}
          </ListItemIcon>
          <ListItemText className="main-button-text" primary={label} sx={{ opacity: opened ? 1 : 0 }} />
          {getExpandArrow(items, opened, openedMenu)}
        </MainButton>
      )}
      {(items && items.length && !opened && (
        <Menu
          className={`navbar-hover-menu${subMenu ? ' submenu' : ''}${toTop ? ' totop' : ''}${selected ? ' selected' : ''}`}
          open={openSubmenu}
          anchorEl={anchorEl.current ? anchorEl.current : null}
          placement="right-start"
          onListMouseEnter={handlePopoverHover}
          onListMouseLeave={handleRequestPopoverClose}
        >
          {createMenuItems(items)}
        </Menu>
      )) ||
        null}
    </>
  );
}

export default NavbarMenu;
